/**
 * Flag someone from the new console (client record and Full Booking). Traci, Oct 2026: who (the client, or from a
 * booking any of its talent), why, and the department that owns it; no type, severity or person. Any team can flag
 * from the booking without opening the client or talent pages. The department is notified and the flag shows in the
 * Notification Center.
 */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Flag } from 'lucide-react';
import { Button } from '@/components/admin/ui/button';
import { Textarea } from '@/components/admin/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { FLAG_DEPARTMENTS } from '@/lib/staging/flags';
import { stagingFetch } from '../api';

const select = 'mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm';
type TalentOption = { talent_id: string; name?: string | null; role_label?: string | null };

export default function NewFlagDialog({ clientId, clientName, bookingId, talent = [] }: { clientId: string; clientName?: string | null; bookingId?: string | null; talent?: TalentOption[] }) {
  const [open, setOpen] = useState(false);
  const empty = { who: 'client', reason: '', department: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const flaggable = talent.filter((t) => t.talent_id);
  const ready = form.reason.trim().length >= 3 && form.department;
  const whoName = form.who === 'client' ? clientName || 'Client' : flaggable.find((t) => t.talent_id === form.who)?.name || 'Talent';

  const save = async () => {
    setSaving(true);
    try {
      const subject = form.who === 'client' ? { client_id: clientId } : { talent_id: form.who };
      await stagingFetch('/api/admin/staging/flags', { method: 'POST', body: JSON.stringify({ ...subject, reason: form.reason, department: form.department, booking_id: bookingId || null }) });
      toast.success(`${whoName} flagged`);
      setOpen(false);
      setForm(empty);
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}><Flag className="h-3.5 w-3.5 mr-1" /> Flag</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Flag</DialogTitle><DialogDescription>The flag goes to the department you pick.{bookingId ? ' The booking reference is added to the flag.' : ''}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {flaggable.length > 0 && (
              <label className="block text-sm">Who<select aria-label="Who" className={select} value={form.who} onChange={(e) => setForm({ ...form, who: e.target.value })}>
                <option value="client">{clientName || 'Client'} (client)</option>
                {flaggable.map((t) => <option key={t.talent_id} value={t.talent_id}>{t.name || 'Talent'}{t.role_label ? ` (${t.role_label})` : ''}</option>)}
              </select></label>
            )}
            <label className="block text-sm">Reason<Textarea aria-label="Reason" className="mt-1" rows={3} placeholder="Why are you flagging?" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
            <label className="block text-sm">Department<select aria-label="Department" className={select} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}><option value="">Select department…</option>{FLAG_DEPARTMENTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}</select></label>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button disabled={!ready || saving} onClick={save}>Create Flag</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
